<template>
  <label class="sp-radio-button">
    <input class="sp-radio-button__input" v-model="model" type="radio" v-bind="props" />
    <svg class="sp-radio-button__input-icon" width="24px" height="24px" viewBox="0 0 24 24">
      <circle class="radio-outline" cx="12" cy="12" r="10" fill="none" stroke="black" />
      <circle class="radio-dot" cx="12" cy="12" r="7" fill="black" />
    </svg>
    <div class="sp-radio-button__label">
      {{ label }}
    </div>
  </label>
</template>
<script setup>
import { computed } from "vue";

const emit = defineEmits(["update:modelValue"]);

const props = defineProps({
  modelValue: {
    type: String,
  },
  id: {
    type: String,
  },
  name: {
    type: String,
  },
  value: {
    type: String,
  },
  label: {
    type: String,
    required: true,
  },
});

const model = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});
</script>
<style lang="scss" scoped>
.sp-radio-button {
  --gap: var(--sp-dtv-radio-btn-gap, 1ch);
  --active-color: var(--sp-dtv-radio-btn-active-color, var(--sp-sys-color-primary, #00818a));
  --label-text-color: var(--sp-dtv-radio-btn-label-text-color, var(--sp-sys-color-primary, #00818a));
  --radio-outline-color: var(--sp-dtv-radio-btn-input-outline-color, var(--sp-sys-form-field-border-color, #aaa));
  --radio-outline-size: var(--sp-dtv-radio-btn-input-outline-size, 2px);
  --radio-dot-color: var(--sp-dtv-radio-btn-input-dot-color, var(--sp-sys-form-field-border-color, #aaa));
  --radio-input-size: var(--sp-dtv-radio-btn-input-size, 1.25rem);
  --radio-animation-time: var(--sp-dtv-radio-btn-animation-time, 120ms);

  display: grid;
  position: relative;
  width: 100%;
  grid-template-columns: auto 1fr;
  grid-template-areas: "input label";
  gap: var(--gap);
  margin-bottom: 0;
  min-height: var(--sp-dtv-radio-btn-min-height, 3rem);
  cursor: pointer;
  align-items: center;
  padding-inline: var(--sp-dtv-radio-btn-padding-inline, 0);
  padding-block: var(--sp-dtv-radio-btn-padding-block, 0);
}
.radio-outline {
  stroke: var(--radio-outline-color);
  stroke-width: var(--radio-outline-size);
  transition: var(--radio-animation-time) stroke ease-in-out;
}

.radio-dot {
  fill: var(--active-color);
  transform: scale(0);
  transform-origin: center;
  transition: var(--radio-animation-time) transform ease-in-out;
}
.sp-radio-button__input-icon {
  --size: var(--radio-input-size);
  grid-area: input;
  cursor: pointer;
  width: var(--size);
  height: var(--size);
}

.sp-radio-button__input {
  -webkit-appearance: none;
  appearance: none;
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;

  &:focus {
    outline: none;
    --border-color: var(--active-color);
    & + .sp-radio-button__input-icon {
      .radio-outline {
        --radio-outline-color: var(--active-color);
      }
    }
  }

  &:checked {
    --border-color: var(--active-color);
    & + .sp-radio-button__input-icon {
      .radio-outline {
        --radio-outline-color: var(--active-color);
      }
      .radio-dot {
        --radio-dot-color: var(--active-color);
        transform: scale(1);
      }
    }

    &::before {
      transform: scale(1);
    }
  }
}

.sp-radio-button__label {
  display: block;
  font-size: var(--sp-dtv-radio-btn-label-font-size, var(--sp-sys-body-font-size, 0.875rem));
  font-weight: var(--sp-dtv-radio-btn-label-font-weight, 500);
  line-height: var(--sp-dtv-radio-btn-label-line-height, 1.1);
  color: var(--label-text-color);
  padding-block: 0.5rem;
}
</style>
